import React, { forwardRef, useState, ReactNode } from 'react'
import classNames from 'classnames'
import { getInputBaseClasses, getInputLabelClasses, getInputHelperTextClasses } from '../utils/inputStyles'

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  type?: 'text' | 'number' | 'email' | 'tel' | 'date' | 'url' | 'file' | 'password' | 'search'
  label?: string
  error?: boolean
  helperText?: string
  leftIcon?: ReactNode
}

// Componente interno para Password
const PasswordInput = forwardRef<HTMLInputElement, Omit<InputProps, 'type'>>(
  ({ label, error, helperText, className = '', disabled, value, onChange, name, leftIcon, id, ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false)
    const hasLeftIcon = !!leftIcon
    
    const inputClasses = classNames(
      getInputBaseClasses(error, disabled, hasLeftIcon, '[&::-ms-reveal]:hidden pr-10'),
      className
    )

    return (
      <div className="w-full">
        {label && (
          <label 
            htmlFor={id} 
            className={getInputLabelClasses()}
          >
            {label}
          </label>
        )}
        <div className="relative mt-0">
          <input
            ref={ref}
            id={id}
            type={showPassword ? 'text' : 'password'}
            className={inputClasses}
            disabled={disabled}
            value={value}
            onChange={onChange}
            name={name}
            {...props}
          />
          {leftIcon && (
            <div 
              className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400" 
              style={{ pointerEvents: 'none', zIndex: 1 }}
            >
              {leftIcon}
            </div>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setShowPassword(!showPassword)
            }}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
            style={{ zIndex: 2 }}
            tabIndex={-1}
          >
            {showPassword ? (
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
              </svg>
            ) : (
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            )}
          </button>
        </div>
        {helperText && (
          <p className={getInputHelperTextClasses(error)}>
            {helperText}
          </p>
        )}
      </div>
    )
  }
)

PasswordInput.displayName = 'PasswordInput'

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ type = 'text', label, error, helperText, className = '', disabled, value, onChange, name, leftIcon, id, ...props }, ref) => {
    const hasLeftIcon = !!leftIcon
    
    const inputClasses = getInputBaseClasses(error, disabled, hasLeftIcon, className)

    // Password para type="password"
    if (type === 'password') {
      return (
        <PasswordInput
          ref={ref}
          id={id}
          label={label}
          error={error}
          helperText={helperText}
          className={className}
          disabled={disabled}
          value={value}
          onChange={onChange}
          name={name}
          leftIcon={leftIcon}
          {...props}
        />
      )
    }

    return (
      <div className="w-full">
        {label && (
          <label 
            htmlFor={id} 
            className={getInputLabelClasses()}
          >
            {label}
          </label>
        )}
        <div className="relative mt-0">
          <input
            ref={ref}
            id={id}
            type={type}
            disabled={disabled}
            className={inputClasses}
            value={value}
            onChange={onChange}
            name={name}
            {...props}
          />
          {leftIcon && (
            <div 
              className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400" 
              style={{ pointerEvents: 'none', zIndex: 1 }}
            >
              {leftIcon}
            </div>
          )}
        </div>
        {helperText && (
          <p className={getInputHelperTextClasses(error)}>
            {helperText}
          </p>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

export default Input
